import React, { useEffect, useMemo, useState } from "react";
import { formatApiError } from "@/lib/api";
import { users as usersApi } from "@/api";
import { toast } from "sonner";
import {
  Plus,
  Trash,
  X,
  LockOpen,
  ShieldCheck,
  EnvelopeOpen,
  DotsThreeVertical,
  CaretDown,
} from "@phosphor-icons/react";
import {
  PAGE_KEYS_BY_MODULE,
  grantableModulesForRole,
  hasModuleAccess,
  normalizeAccessPermissions,
  userAccessPermissions,
} from "@/lib/accessPermissions";
import { useAuth } from "@/context/AuthContext";
import { SortableTableHeader } from "@/components/ui/SortableTableHeader";
import {
  ActaActionMenu,
  ActaActionMenuItem,
  ActaActionSheet,
} from "@/components/ui/ActaActionMenu";
import { useTableSort } from "@/hooks/useTableSort";
import UserMailSettingsDialog from "@/components/mail/UserMailSettingsDialog";
import { sortRows } from "@/lib/tableSort";

const ROLE_LABEL = {
  admin: "Admin",
  manager: "Manager",
  maintenance_manager: "Responsable de maintenance",
  user: "Commercial",
  technician: "Technicien",
};

const ACCESS_MODULE_LABELS = {
  documents: "Documents",
  commercial: "Commercial",
  gmao: "GMAO",
  mail: "Messagerie",
};

const ACCESS_MODULE_DESCRIPTIONS = {
  documents:
    "Documents, dossiers et espace documentaire.",
  commercial:
    "Portefeuille, pipeline, devis, agenda et pilotage commercial.",
  gmao:
    "SAV, interventions, maintenance et parc machines.",
  mail:
    "Messagerie personnelle de l’utilisateur.",
};

const ACCESS_PAGE_LABELS = {
  documents: {
    documents: "Documents et dossiers",
  },
  commercial: {
    dashboard: "Vue d’ensemble",
    pipeline: "Pipeline",
    quotes: "Devis",
    agenda: "Agenda",
    portfolio: "Portefeuille",
    stats: "Statistiques",
    map: "Carte",
  },
  gmao: {
    dashboard: "Vue d’ensemble",
    sav: "Tickets SAV",
    interventions: "Interventions",
    planning: "Planning",
    maintenance: "Maintenance préventive",
    machines: "Parc machines",
    parts: "Stock et pièces",
  },
  mail: {
    mail: "Messagerie",
  },
};

const isManagedAlmaMailbox = (email) => {
  const normalized =
    String(email || "")
      .trim()
      .toLowerCase();
  const parts = normalized.split("@");

  return (
    parts.length === 2
    && Boolean(parts[0])
    && parts[1] === "almafrance.fr"
  );
};


const ROLE_COLOR = {
  admin: "bg-[var(--do-ink)] text-[var(--do-on-accent)]",
  manager: "border border-[var(--lirah-primary)] text-[var(--lirah-primary)]",
  maintenance_manager: "border border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-ink)]",
  user: "border border-[var(--do-border)] text-[var(--do-ink)]",
  technician: "border border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-ink)]",
};

export default function Users({
  embedded = false,
}) {
  const { user: me } = useAuth();
  const defaultRole =
    me?.role === "maintenance_manager"
      ? "technician"
      : "user";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [accessUser, setAccessUser] = useState(null);
  const [accessRole, setAccessRole] = useState(null);
  const [accessRoles, setAccessRoles] = useState([]);
  const [accessForm, setAccessForm] = useState(null);
  const [accessSaving, setAccessSaving] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [mobileActionUser, setMobileActionUser] = useState(null);
  const [mailUser, setMailUser] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    role: defaultRole,
    manager_id: "",
    phone: "",
    title: "",
  });

  const {
    sortBy,
    sortOrder,
    toggleSort,
  } = useTableSort("name", "asc");

  const sortedUsers = useMemo(
    () =>
      sortRows(users, {
        sortBy,
        sortOrder,
        accessors: {
          name: (item) =>
            `${item.firstName || ""} ${item.lastName || ""}`.trim(),
          email: (item) => item.email || "",
          phone: (item) => item.phone || "",
          role: (item) => item.role || "",
          security: (item) =>
            item.isLocked ? 1 : 0,
          status: (item) =>
            item.mustChangePassword ? 0 : 1,
          createdAt: (item) =>
            item.createdAt
              ? new Date(item.createdAt).getTime()
              : null,
        },
      }),
    [users, sortBy, sortOrder]
  );

  const load = async () => {
    setLoading(true);
    try { setUsers(await usersApi.list()); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim().toLowerCase(),
      role: form.role || "user",
      manager_id: form.manager_id || null,
      phone: form.phone?.trim() || "",
      title: form.title?.trim() || "",
    };

    if (!payload.first_name || !payload.last_name || !payload.email) {
      toast.error("Prénom, nom et email sont obligatoires");
      return;
    }

    try {
      await usersApi.create(payload);
      toast.success("Utilisateur créé. Un mot de passe temporaire a été envoyé par e-mail.");
      setOpen(false);
      setForm({
        first_name: "",
        last_name: "",
        email: "",
        role: defaultRole,
        manager_id: "",
        phone: "",
        title: "",
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openAccessEditor = (target) => {
    setAccessUser(target);
    setAccessRole(target.role);
    setAccessRoles(target.roles || [target.role]);
    setAccessForm(
      userAccessPermissions(target)
    );
  };

  const changeAccessRole = (nextRole) => {
    setAccessRole(nextRole);
    setAccessRoles((current) => [...new Set([...current.filter((r) => r !== accessRole), nextRole])]);
    // Role changes must not silently discard admin-selected page access.
    // Backend normalization validates the final selection on save.
  };

  const toggleModule = (module) => {
    setAccessForm((current) => {
      const enabled =
        !current.modules[module];

      return {
        ...current,
        modules: {
          ...current.modules,
          [module]: enabled,
        },
        pages: {
          ...current.pages,
          [module]: Object.fromEntries(
            PAGE_KEYS_BY_MODULE[module].map(
              (page) => [
                page,
                enabled,
              ]
            )
          ),
        },
      };
    });
  };

  const toggleAccessPage = (
    module,
    page
  ) => {
    setAccessForm((current) => ({
      ...current,
      pages: {
        ...current.pages,
        [module]: {
          ...current.pages[module],
          [page]:
            !current.pages[module][page],
        },
      },
    }));
  };

  const saveAccess = async () => {
    setAccessSaving(true);
    try {
      await usersApi.update(accessUser.id, {
        role: accessRole,
        roles: accessRoles,
        access_permissions: accessForm,
      });
      toast.success("Accès utilisateur mis à jour");
      setAccessUser(null);
      setAccessRole(null);
      setAccessRoles([]);
      setAccessForm(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAccessSaving(false);
    }
  };

  const openUserActions = (event, target) => {
    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 767px)").matches;

    if (isMobile) {
      setActionMenu(null);
      setMobileActionUser(target);
      return;
    }

    setMobileActionUser(null);
    setActionMenu({
      user: target,
      anchorEl: event.currentTarget,
    });
  };

  const closeUserActions = () => {
    setActionMenu(null);
    setMobileActionUser(null);
  };

  const runAccessAction = (target) => {
    closeUserActions();
    openAccessEditor(target);
  };

  const runMailAction = (target) => {
    closeUserActions();
    setMailUser(target);
  };

  const runUnlockAction = (target) => {
    closeUserActions();
    unlock(target);
  };

  const runDeleteAction = (target) => {
    closeUserActions();
    remove(target);
  };

  // acta_patch_user_delete_cleanup_v1
  const remove = async (u) => {
    const label = `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email;
    if (!window.confirm(`Supprimer ${label} ?\n\nSes affectations actives seront nettoyées, mais l'historique métier sera conservé.`)) return;

    try {
      const result = await usersApi.remove(u.id);
      const unassigned = result?.cleanup?.prospectsUnassigned || 0;
      toast.success(
        unassigned > 0
          ? `Utilisateur supprimé. ${unassigned} prospect(s) désassigné(s).`
          : "Utilisateur supprimé"
      );
      load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unlock = async (u) => {
    if (!window.confirm(`Déverrouiller le compte ${u.email} ?`)) return;
    try {
      await usersApi.unlock(u.id);
      toast.success("Compte déverrouillé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div
      className={
        embedded
          ? "w-full min-w-0"
          : "p-8 max-w-[1400px]"
      }
      data-testid="users-page"
      data-embedded={
        embedded
          ? "true"
          : "false"
      }
    >
      <div className="users-settings-header mb-4 flex justify-end">
        <button onClick={() => setOpen(true)} data-testid="new-user-btn"
                className="bg-[var(--lirah-primary)] hover:bg-[var(--lirah-primary-hover)] text-[var(--do-on-accent)] text-sm font-heading font-semibold px-3 py-2 inline-flex items-center gap-2">
          <Plus size={15} weight="bold" />
          {me.role === "maintenance_manager"
            ? "Nouveau technicien"
            : "Nouvel utilisateur"}
        </button>
      </div>

      {loading ? <div className="label-eyebrow lirah-pulse">Chargement</div> : (
        <div className="lirah-card users-desktop-table overflow-hidden" data-testid="users-table">
          <div className="table-responsive table-scroll">
          <table className="w-full text-sm">
            <thead className="bg-[var(--do-bg-solid)] border-b border-[var(--do-border)]">
              <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[var(--do-muted)]">
                <SortableTableHeader label="Nom" field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-2.5 font-semibold" testId="users-sort-name" />
                <SortableTableHeader label="Email" field="email" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-2.5 font-semibold" testId="users-sort-email" />
                <SortableTableHeader label="WhatsApp" field="phone" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-2.5 font-semibold" testId="users-sort-phone" />
                <SortableTableHeader label="Rôle" field="role" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-2.5 font-semibold" testId="users-sort-role" />
                <SortableTableHeader label="Sécurité" field="security" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-2.5 font-semibold" testId="users-sort-security" />
                <SortableTableHeader label="État" field="status" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-2.5 font-semibold" testId="users-sort-status" />
                <SortableTableHeader label="Créé" field="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-2.5 font-semibold" testId="users-sort-createdAt" />
                <th className="px-4 py-2.5 font-semibold text-right w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u) => (
                <tr key={u.id} className="border-b border-[var(--do-border)] hover:bg-[var(--do-bg-solid)]" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-2.5 font-medium">{u.firstName} {u.lastName}{u.id === me.id && <span className="ml-2 text-[11px] text-[var(--do-muted)]">(vous)</span>}</td>
                  <td className="px-4 py-2.5 text-[var(--do-muted)] text-xs">{u.email}</td>
                  <td className="px-4 py-2.5 text-[var(--do-muted)] text-xs font-mono-stat">{u.phone || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[11px] px-2 py-0.5 font-heading font-semibold uppercase tracking-wider ${ROLE_COLOR[u.role] || ""}`}>{(u.roles || [u.role]).map((role) => ROLE_LABEL[role] || role).join(" · ")}</span>
                    {u.role === "maintenance_manager"
                      && hasModuleAccess(u, "commercial") && (
                      <span className="ml-1 text-[11px] px-2 py-0.5 font-heading font-semibold uppercase tracking-wider border border-[var(--do-border)] text-[var(--do-ink)]">
                        Commercial
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    {u.isLocked ? (
                      <div className="space-y-1">
                        <span className="inline-flex px-2 py-0.5 rounded-full bg-[var(--do-accent-soft)] text-[var(--do-accent)] border border-[var(--do-border)] font-heading font-semibold">
                          Verrouillé
                        </span>
                        <div className="text-[var(--do-muted)] font-mono-stat">
                          Jusqu'au {new Date(u.lockedUntil).toLocaleString("fr-FR")}
                        </div>
                      </div>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-[var(--do-accent-soft)] text-[var(--do-accent)] border border-[var(--do-border)] font-heading font-semibold">
                        Actif
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    {u.mustChangePassword ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-[var(--do-accent-soft)] text-[var(--do-accent)] border border-[var(--do-border)] font-heading font-semibold">
                        Première connexion requise
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-[var(--do-accent-soft)] text-[var(--do-accent)] border border-[var(--do-border)] font-heading font-semibold">
                        Actif
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-[var(--do-muted)] font-mono-stat text-xs">{new Date(u.createdAt).toLocaleDateString("fr-FR")}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div
                      className="documents-v2-action-menu"
                      data-users-action-menu
                    >
                      <button
                        type="button"
                        className="acta-action-trigger documents-v2-action-trigger"
                        aria-expanded={
                          actionMenu?.user?.id === u.id
                        }
                        onClick={(event) =>
                          openUserActions(event, u)
                        }
                        data-testid={`actions-user-${u.id}`}
                      >
                        <DotsThreeVertical
                          size={16}
                          weight="bold"
                        />
                        Actions
                        <CaretDown size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {sortedUsers.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-[var(--do-muted)] text-sm">Aucun utilisateur.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {!loading && (
        <div className="users-mobile-cards" data-testid="users-mobile-cards">
          {sortedUsers.length === 0 ? (
            <div className="lirah-card p-6 text-center text-sm text-[var(--do-muted)]">
              Aucun utilisateur.
            </div>
          ) : sortedUsers.map((u) => (
            <article key={`mobile-${u.id}`} className="lirah-card p-4" data-testid={`user-mobile-card-${u.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-heading text-sm font-black text-[var(--do-ink)]">
                    {u.firstName} {u.lastName}{u.id === me.id && <span className="ml-1 text-[10px] text-[var(--do-muted)]">(vous)</span>}
                  </div>
                  <div className="mt-1 break-all text-xs text-[var(--do-muted)]">{u.email}</div>
                </div>
                <span className={`shrink-0 text-[10px] px-2 py-1 font-heading font-semibold uppercase tracking-wider ${ROLE_COLOR[u.role] || ""}`}>
                  {(u.roles || [u.role]).map((role) => ROLE_LABEL[role] || role).join(" · ")}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                <div><span className="text-[var(--do-muted)]">Sécurité</span><div className="mt-1 font-bold text-[var(--do-ink)]">{u.isLocked ? "Verrouillé" : "Actif"}</div></div>
                <div><span className="text-[var(--do-muted)]">Créé le</span><div className="mt-1 font-bold text-[var(--do-ink)]">{new Date(u.createdAt).toLocaleDateString("fr-FR")}</div></div>
              </div>
              <button
                type="button"
                className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-xs font-heading font-bold"
                onClick={(event) => openUserActions(event, u)}
                data-testid={`mobile-actions-user-${u.id}`}
              >
                <DotsThreeVertical size={17} weight="bold" />
                Actions
              </button>
            </article>
          ))}
        </div>
      )}

      <ActaActionMenu
        open={Boolean(actionMenu)}
        anchorEl={actionMenu?.anchorEl}
        onClose={() => setActionMenu(null)}
        width={240}
        estimatedHeight={190}
        testId="users-action-menu"
      >
        {actionMenu?.user
          && me.role === "admin"
          && actionMenu.user.role !== "admin"
          && (
          <ActaActionMenuItem
            icon={<ShieldCheck size={18} />}
            label="Gérer les accès"
            onClick={() =>
              runAccessAction(actionMenu.user)
            }
            testId={`access-user-${actionMenu.user.id}`}
          />
        )}

        {actionMenu?.user
          && me.role === "admin"
          && !isManagedAlmaMailbox(
            actionMenu.user.email
          )
          && (
          <ActaActionMenuItem
            icon={<EnvelopeOpen size={18} />}
            label="Configurer la messagerie"
            onClick={() =>
              runMailAction(actionMenu.user)
            }
            testId={`mail-user-${actionMenu.user.id}`}
          />
        )}

        {actionMenu?.user?.isLocked && (
          <ActaActionMenuItem
            icon={<LockOpen size={18} />}
            label="Déverrouiller"
            onClick={() =>
              runUnlockAction(actionMenu.user)
            }
            testId={`unlock-user-${actionMenu.user.id}`}
          />
        )}

        {actionMenu?.user &&
          actionMenu.user.id !== me.id && (
            <ActaActionMenuItem
              icon={<Trash size={18} />}
              label="Supprimer"
              danger
              onClick={() =>
                runDeleteAction(actionMenu.user)
              }
              testId={`delete-user-${actionMenu.user.id}`}
            />
          )}
      </ActaActionMenu>

      <ActaActionSheet
        open={Boolean(mobileActionUser)}
        title="Actions"
        subtitle={
          mobileActionUser
            ? `${mobileActionUser.firstName || ""} ${
                mobileActionUser.lastName || ""
              }`.trim() || mobileActionUser.email
            : ""
        }
        onClose={() => setMobileActionUser(null)}
        testId="users-mobile-action-sheet"
      >
        {mobileActionUser
          && me.role === "admin"
          && mobileActionUser.role !== "admin"
          && (
          <ActaActionMenuItem
            icon={<ShieldCheck size={18} />}
            label="Gérer les accès"
            onClick={() =>
              runAccessAction(mobileActionUser)
            }
            testId={`mobile-access-user-${mobileActionUser.id}`}
          />
        )}

        {mobileActionUser
          && me.role === "admin"
          && !isManagedAlmaMailbox(
            mobileActionUser.email
          )
          && (
          <ActaActionMenuItem
            icon={<EnvelopeOpen size={18} />}
            label="Configurer la messagerie"
            onClick={() =>
              runMailAction(mobileActionUser)
            }
            testId={`mobile-mail-user-${mobileActionUser.id}`}
          />
        )}

        {mobileActionUser?.isLocked && (
          <ActaActionMenuItem
            icon={<LockOpen size={18} />}
            label="Déverrouiller"
            onClick={() =>
              runUnlockAction(mobileActionUser)
            }
            testId={`mobile-unlock-user-${mobileActionUser.id}`}
          />
        )}

        {mobileActionUser &&
          mobileActionUser.id !== me.id && (
            <ActaActionMenuItem
              icon={<Trash size={18} />}
              label="Supprimer"
              danger
              onClick={() =>
                runDeleteAction(mobileActionUser)
              }
              testId={`mobile-delete-user-${mobileActionUser.id}`}
            />
          )}
      </ActaActionSheet>

      {mailUser && (
        <UserMailSettingsDialog
          user={mailUser}
          onClose={() =>
            setMailUser(null)
          }
          onSaved={async () => {
            await load();
            setMailUser(null);
          }}
        />
      )}

      {accessUser && accessRole && accessForm && (
        <div className="acta-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6" data-testid="user-access-modal">
          <div className="acta-modal-surface acta-modal-scroll w-full max-w-2xl max-h-[90dvh] rounded-3xl">
            <div className="px-5 py-3 border-b border-[var(--do-border)] flex items-center justify-between">
              <div>
                <div className="font-heading font-bold text-sm">
                  Accès de {accessUser.firstName} {accessUser.lastName}
                </div>
                <div className="text-xs text-[var(--do-muted)]">
                  {ROLE_LABEL[accessRole] || accessRole}
                </div>
              </div>
              <button type="button" onClick={() => { setAccessUser(null); setAccessRole(null); setAccessForm(null); }} className="p-1.5 hover:bg-[var(--do-bg-2)]">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-6">
              <p className="text-xs text-[var(--do-muted)]">
                Le rôle principal assure la compatibilité des workflows existants. Plusieurs rôles métier peuvent être attribués, avec des accès aux modules et pages sélectionnés par l’administrateur.
              </p>

              <div>
                <label className="label-eyebrow block mb-1">
                  Rôle principal
                </label>
                <select
                  value={accessRole}
                  onChange={(event) =>
                    changeAccessRole(event.target.value)
                  }
                  className="w-full px-3 py-2 border border-[var(--do-border)] text-sm bg-[var(--do-bg-solid)] focus:outline-none focus:border-[var(--lirah-primary)]"
                  data-testid="access-primary-role"
                >
                  <option value="user">Commercial</option>
                  <option value="technician">Technicien</option>
                  <option value="manager">Manager commercial</option>
                  <option value="maintenance_manager">
                    Responsable de maintenance
                  </option>
                </select>
              </div>

              <fieldset className="space-y-2" data-testid="access-roles">
                <legend className="label-eyebrow mb-2">Rôles métier cumulés</legend>
                {[
                  ["user", "Commercial"],
                  ["technician", "Technicien"],
                  ["manager", "Manager commercial"],
                  ["maintenance_manager", "Responsable de maintenance"],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-3 text-sm">
                    <input
                      type="checkbox"
                      checked={accessRoles.includes(key)}
                      disabled={key === accessRole}
                      onChange={(event) => setAccessRoles((current) =>
                        event.target.checked ? [...current, key] : current.filter((role) => role !== key)
                      )}
                      data-testid={`access-role-${key}`}
                    />
                    {label}
                  </label>
                ))}
              </fieldset>

              {[...new Set(accessRoles.flatMap(grantableModulesForRole))].map((module) => (
                <section
                  key={module}
                  className="space-y-3"
                  data-testid={`access-section-${module}`}
                >
                  <div>
                    <div className="label-eyebrow mb-2">
                      Module {ACCESS_MODULE_LABELS[module]}
                    </div>

                    <label className="flex items-start gap-3 rounded-xl border border-[var(--do-border)] px-3 py-3">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={
                          accessForm.modules[module]
                        }
                        onChange={() =>
                          toggleModule(module)
                        }
                        data-testid={`access-module-${module}`}
                      />

                      <span>
                        <span className="block text-sm font-heading font-semibold">
                          Autoriser l’accès au module{" "}
                          {ACCESS_MODULE_LABELS[module]}
                        </span>

                        <span className="mt-1 block text-xs text-[var(--do-muted)]">
                          {ACCESS_MODULE_DESCRIPTIONS[module]}
                        </span>
                      </span>
                    </label>
                  </div>

                  <div>
                    <div className="label-eyebrow mb-2">
                      Pages {ACCESS_MODULE_LABELS[module]}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {PAGE_KEYS_BY_MODULE[module].map(
                        (page) => (
                          <label
                            key={page}
                            className="flex items-center gap-3 rounded-xl border border-[var(--do-border)] px-3 py-3"
                          >
                            <input
                              type="checkbox"
                              disabled={
                                !accessForm.modules[module]
                              }
                              checked={
                                accessForm.pages[module][page]
                              }
                              onChange={() =>
                                toggleAccessPage(
                                  module,
                                  page
                                )
                              }
                              data-testid={`access-page-${module}-${page}`}
                            />

                            <span className="text-sm font-heading font-semibold">
                              {ACCESS_PAGE_LABELS[module][page]}
                            </span>
                          </label>
                        )
                      )}
                    </div>
                  </div>
                </section>
              ))}

              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => { setAccessUser(null); setAccessRole(null); setAccessForm(null); }} className="px-3 py-2 text-sm text-[var(--do-muted)]">Annuler</button>
                <button type="button" onClick={saveAccess} disabled={accessSaving} className="bg-[var(--lirah-primary)] disabled:opacity-60 text-[var(--do-on-accent)] px-4 py-2 text-sm font-heading font-semibold">
                  {accessSaving ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="acta-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6" data-testid="user-modal">
          <div className="acta-modal-surface acta-modal-scroll w-full max-w-md max-h-[90dvh] rounded-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-[var(--do-border)] flex items-center justify-between">
              <div className="font-heading font-bold text-sm">Nouvel utilisateur</div>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-[var(--do-bg-2)]"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label-eyebrow block mb-1">Prénom *</label>
                  <input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="user-firstname-input"
                         className="w-full px-3 py-2 border border-[var(--do-border)] text-sm focus:outline-none focus:border-[var(--lirah-primary)]" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-1">Nom *</label>
                  <input value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="user-lastname-input"
                         className="w-full px-3 py-2 border border-[var(--do-border)] text-sm focus:outline-none focus:border-[var(--lirah-primary)]" />
                </div>
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="user-email-input"
                       className="w-full px-3 py-2 border border-[var(--do-border)] text-sm focus:outline-none focus:border-[var(--lirah-primary)]" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label-eyebrow block mb-1">WhatsApp professionnel</label>
                  <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                         placeholder="+33 6 12 34 56 78" data-testid="user-phone-input"
                         className="w-full px-3 py-2 border border-[var(--do-border)] text-sm focus:outline-none focus:border-[var(--lirah-primary)]" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-1">Fonction</label>
                  <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                         placeholder="Commercial · Manager · Responsable maintenance · Technicien…" data-testid="user-title-input"
                         className="w-full px-3 py-2 border border-[var(--do-border)] text-sm focus:outline-none focus:border-[var(--lirah-primary)]" />
                </div>
              </div>
              <div className="text-[11px] text-[var(--do-muted)] -mt-1">Le numéro est utilisé pour le bouton WhatsApp sur les liens de partage. La fonction apparaît sous le nom dans le bloc "Contacter l\'expéditeur".</div>
              <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-2 text-[12px] text-[var(--do-ink)]">Un mot de passe temporaire sera généré automatiquement et envoyé par e-mail. L’utilisateur devra le modifier à sa première connexion.</div>
              <div>
                <label className="label-eyebrow block mb-1">Rôle</label>
                <select
                  value={form.role}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      role: e.target.value,
                      manager_id: "",
                    }))
                  }
                  data-testid="user-role-select"
                  className="w-full px-3 py-2 border border-[var(--do-border)] text-sm bg-[var(--do-bg-solid)] focus:outline-none focus:border-[var(--lirah-primary)]"
                >
                  {me.role === "manager" && (
                    <option value="user">
                      Commercial
                    </option>
                  )}

                  {me.role === "maintenance_manager" && (
                    <option value="technician">
                      Technicien
                    </option>
                  )}

                  {me.role === "admin" && (
                    <>
                      <option value="user">
                        Commercial
                      </option>
                      <option value="technician">
                        Technicien
                      </option>
                      <option value="manager">
                        Manager commercial
                      </option>
                      <option value="maintenance_manager">
                        Responsable de maintenance
                      </option>
                      <option value="admin">
                        Administrateur
                      </option>
                    </>
                  )}
                </select>
              </div>

              {me.role === "admin" &&
                ["user", "technician"].includes(
                  form.role
                ) && (
                  <div>
                    <label className="label-eyebrow block mb-1">
                      {form.role === "technician"
                        ? "Responsable de maintenance"
                        : "Manager commercial"}
                    </label>

                    <select
                      value={form.manager_id}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          manager_id: e.target.value,
                        }))
                      }
                      data-testid="user-manager-select"
                      className="w-full px-3 py-2 border border-[var(--do-border)] text-sm bg-[var(--do-bg-solid)] focus:outline-none focus:border-[var(--lirah-primary)]"
                    >
                      <option value="">
                        Non affecté
                      </option>

                      {users
                        .filter((candidate) =>
                          form.role === "technician"
                            ? candidate.role ===
                              "maintenance_manager"
                            : candidate.role ===
                              "manager"
                        )
                        .map((candidate) => (
                          <option
                            key={candidate.id}
                            value={candidate.id}
                          >
                            {candidate.firstName}{" "}
                            {candidate.lastName}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setOpen(false)} className="px-3 py-2 text-sm text-[var(--do-muted)]">Annuler</button>
                <button onClick={submit} className="bg-[var(--lirah-primary)] text-[var(--do-on-accent)] px-4 py-2 text-sm font-heading font-semibold" data-testid="user-submit">Créer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
